import QRCode from 'qrcode';
import { Alpine } from '../../core/alpine-core.js';
import { viteEnv } from '../../core/import-meta-env.js';
import { preferredAppOrigin } from '../../core/preferred-app-origin.js';
import { resolveVenueKioskAppOrigin } from '../../public/kiosk-tablet-api.js';
import { apiFetch } from '../api.js';
import { isTrainingScorerEmbedUrl } from '../router-guards.js';
import {
  SCORER_QUICK_PRESET_POOL,
  defaultScorerQuickConfig,
  loadScorerQuickConfig,
  mergeMoreQuickPresets,
  quickPresetButtonLabel,
  saveScorerQuickConfig,
  withNearCheckoutNsOnFourthQuickTop,
} from '../scorer-quick-config.js';
import { nextBotScoreClient } from './training-bot.js';
import { formatPassCriteriaHint } from './training-pass-criteria-hint.js';
import { OPPONENT_THINKING_MIN_MS, withOpponentThinkingUi } from '../../core/opponent-thinking.js';
import {
  SCORER_PLAYER_STATS_CAROUSEL_STATE,
  createScorerPlayerStatsCarouselMethods,
} from '../../shared/scorer-player-stats-carousel.js';
import { createScorerMatchScoreboardBindings } from '../../shared/scorer-match-scoreboard.js';
import { createScorerCoreMethods } from '../../shared/scorer-core.js';
import { checkoutRuleShortLabel } from '../../shared/match-format-rule-labels.js';
import { playerAvatarUrl } from '../player-avatar-url.js';

const TRAINING_SCORER_QUICK_LS = 'pdl.training-scorer.quick';
const PDL_QR_LOGO_SRC = '/assets/darts-league-logo.png';
const PER_DART_DOUBLE_MAX = 20;

/** High EC QR + small centered logo (same asset as nav / fixture scorer popups). */
async function renderQrWithCenterLogo(canvas, text, sizePx) {
  const size = Math.max(64, Math.round(sizePx));
  canvas.width = size;
  canvas.height = size;
  await QRCode.toCanvas(canvas, text, {
    width: size,
    margin: 1,
    color: { dark: '#0a0a0a', light: '#ffffff' },
    errorCorrectionLevel: 'H',
  });
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  try {
    const logo = await new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = PDL_QR_LOGO_SRC;
    });
    if (!logo || !logo.naturalWidth) return;
    const lw = Math.round(size * 0.22);
    const pad = Math.max(2, Math.round(lw * 0.12));
    const cx = (size - lw) / 2;
    const cy = (size - lw) / 2;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(cx - pad, cy - pad, lw + pad * 2, lw + pad * 2);
    ctx.drawImage(logo, cx, cy, lw, lw);
  } catch {
    /* keep plain QR */
  }
}

function parseTrainingSessionId() {
  const parts = location.pathname.split('/').filter(Boolean);
  let idx = parts.indexOf('training-score');
  if (idx >= 0 && parts[idx + 1]) return decodeURIComponent(parts[idx + 1]);
  idx = parts.indexOf('training');
  if (idx < 0 || !parts[idx + 1]) return '';
  const segment = parts[idx + 1];
  if (segment === 'score' || segment === 'launch') return '';
  return decodeURIComponent(segment);
}

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

function scheduleIdleTask(fn, timeoutMs = 700) {
  if (typeof globalThis.requestIdleCallback === 'function') {
    globalThis.requestIdleCallback(fn, { timeout: timeoutMs });
    return;
  }
  globalThis.setTimeout(fn, 0);
}

function legsToWin(bestOf) {
  const n = Math.max(1, Number(bestOf) || 9);
  return Math.ceil(n / 2);
}

Alpine.data('trainingScorer', () => ({
  ...SCORER_PLAYER_STATS_CAROUSEL_STATE,
  ...createScorerCoreMethods({ scorerContext: 'training' }),
  sessionId: '',
  session: null,
  ghostSource: null,
  loading: true,
  /** Venue kiosk iframe (`?embed=1`): compact chrome, no portrait gate. */
  embedMode: false,
  /** `?from=venue-kiosk` when opened from the venue tablet (full page or same-tab). */
  fromVenueKiosk: false,
  /** Venue shell sidebar expanded (synced via postMessage from parent). */
  venueRailExpanded: false,
  /** Parent venue shell sidebar shows Session & goals (`?vkShellGoals=1`); hide duplicate blocks in the iframe. */
  venueKioskShellGoals: false,
  /** Venue iframe: confirm leaving scorer (notify parent → Training hub). */
  venueEmbedLeaveDialogOpen: false,
  error: '',
  scoreInput: '',
  checkoutDarts: '',
  checkoutLabel: '',
  checkoutConfirmOpen: false,
  checkoutConfirmPending: null,
  checkoutConfirmDarts: '3',
  saving: false,
  autoRunTimer: null,
  opponentRunning: false,
  /** Full-screen overlay while rule-bot or ChatGPT posts B’s visit (min visible time). */
  opponentThinkingDialogOpen: false,
  scorerShareQrCanvas: null,
  handleKeydown: null,
  qrResizeTimer: null,
  onQrResize: null,
  initStartPerfMs: 0,
  /** Inline edit for This leg scores (click row score → edit). */
  editingVisitIndex: null,
  editingVisitDraft: '',
  /** Legacy flag — checkout is inline like fixture/challenge scorers. */
  checkoutSheetOpen: false,
  quickScoresExpanded: false,
  /** Kiosk-style "More" quick scores popover */
  quickMoreOpen: false,
  quickMoreFixedPopover: true,
  quickMorePanelStyle: '',
  _scrollLockApplied: false,
  _prevBodyOverflow: '',
  _prevBodyTouchAction: '',
  _prevBodyOverscroll: '',
  _prevDocOverflow: '',
  _prevDocOverscroll: '',
  quickScoreTop: [],
  quickScoreMore: [],
  undoArmed: false,
  undoArmTimer: null,
  _vkShellMessageListener: null,
  /** Full-screen flash after computer / ChatGPT bot scores. */
  opponentRevealOpen: false,
  opponentRevealScore: 0,
  opponentRevealHeadline: '',
  opponentRevealSub: '',
  /** max | hot | great | ton | solid | nice | quiet */
  opponentRevealTone: 'quiet',
  /** computer | chatgpt */
  opponentRevealSource: 'computer',
  /** Solo doubles drill: how many darts in this visit (1–3), then per-dart miss / double. */
  perDartCount: 3,
  /** Each slot: '' unset, 'miss', or '1'…'20' (double bed). */
  perDartSlots: ['', '', ''],

  quickPresetButtonLabel,

  /** Built-in / API computer opponent (not human A/B on one device). */
  isTrainingVsComputer() {
    if (String(this.session?.sourceType || '') === 'bot') return true;
    if (String(this.session?.catalogPracticeMode || '') === 'computer') return true;
    return false;
  },

  /**
   * Solo layout: one scoring card, only side A (mirrors server `isLivePracticeSoloScoring`).
   * Uses catalog template when the session document predates `catalogPracticeMode`.
   */
  isCatalogSolo() {
    if (this.isTrainingVsComputer()) return false;
    if (String(this.session?.sourceType) === 'ghost_from_session') return false;
    if (String(this.session?.sourceType) === 'ai') return false;
    if (String(this.session?.sourceType) !== 'live_practice') return false;
    if (String(this.session?.catalogTemplate?.practiceMode || '').toLowerCase() === 'solo') return true;
    if (String(this.session?.catalogPracticeMode || '') === 'solo') return true;
    if (String(this.session?.catalogPracticeMode || '') === '') return true;
    return false;
  },

  isPerDartDoubles() {
    return String(this.session?.format?.dartEntryMode || '') === 'per_dart_doubles';
  },

  perDartSlotIndices() {
    const n = Math.min(3, Math.max(1, Math.round(Number(this.perDartCount)) || 3));
    return Array.from({ length: n }, (_, i) => i);
  },

  doubleRingOptions() {
    return Array.from({ length: PER_DART_DOUBLE_MAX }, (_, i) => i + 1);
  },

  resetPerDartForm() {
    this.perDartCount = 3;
    this.perDartSlots = ['', '', ''];
  },

  setPerDartVisitCount(n) {
    this.perDartCount = Math.min(3, Math.max(1, Math.round(Number(n)) || 3));
    this.perDartSlots = ['', '', ''];
  },

  setPerDartMiss(idx) {
    if (!this.scoringKeypadActive()) return;
    const i = Number(idx);
    if (!Number.isFinite(i) || i < 0 || i > 2) return;
    const next = [...this.perDartSlots];
    next[i] = 'miss';
    this.perDartSlots = next;
    this.checkoutDarts = '';
  },

  setPerDartDoubleFromSelect(idx, event) {
    if (!this.scoringKeypadActive()) return;
    const raw = event?.target?.value;
    const i = Number(idx);
    if (!Number.isFinite(i) || i < 0 || i > 2) return;
    const next = [...this.perDartSlots];
    if (raw === '' || raw == null) next[i] = '';
    else {
      const n = Math.min(20, Math.max(1, Math.round(Number(raw))));
      next[i] = String(Number.isFinite(n) ? n : '');
    }
    this.perDartSlots = next;
    this.checkoutDarts = '';
  },

  buildDartBreakdownPayload() {
    const n = Math.min(3, Math.max(1, Math.round(Number(this.perDartCount)) || 3));
    const out = [];
    for (let i = 0; i < n; i += 1) {
      const cell = this.perDartSlots[i];
      if (cell === '' || cell == null) return null;
      if (cell === 'miss') out.push('miss');
      else out.push(`d${cell}`);
    }
    return out;
  },

  perDartVisitScore() {
    const lines = this.buildDartBreakdownPayload();
    if (!lines) return Number.NaN;
    let t = 0;
    for (const s of lines) {
      if (s === 'miss') continue;
      const m = /^d(\d+)$/.exec(s);
      if (m) t += 2 * Number(m[1]);
    }
    return Math.min(180, t);
  },

  keypadStatusSubline() {
    if (this.matchFinished()) return 'Match finished.';
    const tr = String(this.session?.liveScoring?.currentTurnRole || '');
    const st = String(this.session?.sourceType || '');
    if (tr === 'B' && (st === 'bot' || st === 'ghost_from_session')) return 'Waiting on opponent visit…';
    if (tr === 'B' && st === 'live_practice' && !this.isCatalogSolo()) return 'Other side scoring';
    return 'Your turn to score.';
  },

  allQuickPresetPool() {
    return [...SCORER_QUICK_PRESET_POOL];
  },

  visibleQuickPresets() {
    return this.quickScoreTop.slice(0, 4);
  },

  paddedVisibleQuickPresets() {
    const v = this.visibleQuickPresets();
    return [0, 1, 2, 3].map((i) => (v[i] != null ? v[i] : null));
  },

  moreQuickPresets() {
    const merged = mergeMoreQuickPresets(this.quickScoreMore);
    return [...merged, { ns: true }];
  },

  hasMoreQuickPresets() {
    return mergeMoreQuickPresets(this.quickScoreMore).length > 0;
  },

  allQuickTop() {
    const t = [...(this.quickScoreTop || [])].slice(0, 4);
    while (t.length < 4) t.push(null);
    const editing = this.editingVisitIndex != null;
    const active = this.liveKeypadEnabled() && !editing;
    const rem = this.currentTurnRemaining();
    return withNearCheckoutNsOnFourthQuickTop(t, rem, { editingVisit: editing, active });
  },

  onQuickTopSlotClick(preset) {
    if (this.isNsQuickPreset(preset)) {
      void this.pickQuickMoreNoScore();
      return;
    }
    this.setScoreInput(preset);
  },

  quickPresetLabel(p) {
    if (p && typeof p === 'object') return '';
    return Number(p) === 0 ? '0' : String(p);
  },

  liveKeypadEnabled() {
    if (this.editingVisitIndex != null) return false;
    return this.scoringKeypadActive() && !this.saving;
  },

  mergeSessionKeepCatalog(prev, next) {
    if (!next) return next;
    const out = { ...next };
    if (!out.catalogTemplate && prev?.catalogTemplate) out.catalogTemplate = prev.catalogTemplate;
    return out;
  },

  persistQuickScores() {
    saveScorerQuickConfig(TRAINING_SCORER_QUICK_LS, { top: this.quickScoreTop, more: this.quickScoreMore });
  },

  toggleQuickTop(preset) {
    const p = Number(preset);
    const top = [...this.quickScoreTop];
    const more = [...this.quickScoreMore];
    const i = top.indexOf(p);
    if (i >= 0) {
      top.splice(i, 1);
    } else if (top.length >= 4) {
      this.$store?.toastr?.info?.('At most 4 scores on the top row.', 2800);
      return;
    } else {
      const mi = more.indexOf(p);
      if (mi >= 0) more.splice(mi, 1);
      top.push(p);
    }
    this.quickScoreTop = top;
    this.quickScoreMore = more;
    this.persistQuickScores();
  },

  toggleQuickMore(preset) {
    const p = Number(preset);
    if (this.quickScoreTop.includes(p)) return;
    const more = [...this.quickScoreMore];
    const i = more.indexOf(p);
    if (i >= 0) more.splice(i, 1);
    else more.push(p);
    more.sort((a, b) => a - b);
    this.quickScoreMore = more;
    this.persistQuickScores();
  },

  resetQuickScoresToDefault() {
    const d = defaultScorerQuickConfig();
    this.quickScoreTop = [...d.top];
    this.quickScoreMore = [...d.more];
    this.persistQuickScores();
    this.$store?.toastr?.success?.('Quick scores reset', 2000);
  },

  shareTrainingScorerUrl() {
    const id = this.session?._id || this.sessionId;
    return id ? `${preferredAppOrigin()}/admin/training/${id}/scorer` : '';
  },

  /** Inline status strip — disabled in venue embed (fixed thinking overlay; avoids layout jump). */
  showOpponentStatusStrip() {
    if (this.embedMode) return false;
    if (this.opponentThinkingDialogOpen) return false;
    if (this.matchFinished()) return false;
    if (String(this.session?.liveScoring?.state) !== 'live') return false;
    const st = String(this.session?.sourceType || '');
    if (st !== 'ghost_from_session' && st !== 'bot') return false;
    const turnB = String(this.session?.liveScoring?.currentTurnRole) === 'B';
    return turnB || this.opponentRunning || this.opponentRevealOpen;
  },

  showTrainingQrStrip() {
    return false;
  },

  /** Second-device QR disabled for training — flow goes straight to this scorer. */
  showScorerShareQrStrip() {
    return false;
  },

  currentLegDartsThrown() {
    const visits = this.currentLeg()?.visits || [];
    return visits.reduce((t, v) => t + Math.max(0, Number(v?.darts || 3)), 0);
  },

  currentLegHighScore() {
    const visits = this.currentLeg()?.visits || [];
    return visits.reduce((best, v) => (v?.bust ? best : Math.max(best, Number(v?.score || 0))), 0);
  },

  historyVisits() {
    return this.currentLegVisitsDesc().map((e) => ({ visit: e.visit, index: e.visitIndex }));
  },

  visitDisplayName(visit) {
    if (!visit) return '';
    if (String(visit.playerRole) === 'A') return this.roleADisplayName();
    if (String(visit.playerRole) === 'B') return this.roleBDisplayName();
    return 'Player';
  },

  trainingStructure() {
    return 'legs';
  },

  headerHomeTeamDisplay() {
    return this.roleADisplayName();
  },

  headerAwayTeamDisplay() {
    return this.roleBDisplayName();
  },

  headerScoreForIndex(i) {
    if (Number(i) === 1) return this.legPointsAway();
    return this.legPointsHome();
  },

  legsNeededToWinMatchTraining() {
    const bo = Math.max(1, Number(this.session?.format?.bestOfLegs) || 9);
    return Math.floor(bo / 2) + 1;
  },

  headerRaceLeftText() {
    if (this.matchFinished()) return '—';
    const need = this.legsNeededToWinMatchTraining();
    const h = this.legPointsHome();
    const a = this.legPointsAway();
    const max = Math.max(h, a);
    const left = Math.max(0, need - max);
    if (left <= 0) return 'Deciding';
    return left === 1 ? '1 leg for match' : `${left} legs for match`;
  },

  maxThrownLimitTraining() {
    return 51;
  },

  legDartsFractionDisplay() {
    return `${this.currentLegDartsThrown()} / ${this.maxThrownLimitTraining()}`;
  },

  sessionCheckoutRuleLabel() {
    return checkoutRuleShortLabel(this.session?.format?.checkout);
  },

  hasTrainingScrollLockOverlays() {
    return !!(this.opponentThinkingDialogOpen || this.opponentRevealOpen || this.venueEmbedLeaveDialogOpen);
  },

  syncTrainingOverlayScrollLock() {
    if (typeof document === 'undefined') return;
    const shouldLock = this.hasTrainingScrollLockOverlays();
    if (shouldLock === this._scrollLockApplied) return;
    const body = document.body;
    const docEl = document.documentElement;
    if (!body || !docEl) return;
    if (shouldLock) {
      this._prevBodyOverflow = body.style.overflow;
      this._prevBodyTouchAction = body.style.touchAction;
      this._prevBodyOverscroll = body.style.overscrollBehavior;
      this._prevDocOverflow = docEl.style.overflow;
      this._prevDocOverscroll = docEl.style.overscrollBehavior;
      body.style.overflow = 'hidden';
      body.style.touchAction = 'none';
      body.style.overscrollBehavior = 'none';
      docEl.style.overflow = 'hidden';
      docEl.style.overscrollBehavior = 'none';
    } else {
      body.style.overflow = this._prevBodyOverflow || '';
      body.style.touchAction = this._prevBodyTouchAction || '';
      body.style.overscrollBehavior = this._prevBodyOverscroll || '';
      docEl.style.overflow = this._prevDocOverflow || '';
      docEl.style.overscrollBehavior = this._prevDocOverscroll || '';
    }
    this._scrollLockApplied = shouldLock;
  },

  toggleQuickMorePanel(ev) {
    if (this.quickMoreOpen) {
      this.closeQuickMore();
      return;
    }
    if (typeof this.closeScorerMobileHistory === 'function') this.closeScorerMobileHistory();
    this.quickMoreOpen = true;
    this.quickMoreFixedPopover =
      !this.embedMode &&
      !this.scorerUseKeypadQuickOverlay() &&
      typeof globalThis.matchMedia === 'function' &&
      globalThis.matchMedia('(min-width: 640px)').matches;
    this.$nextTick(() => {
      if (this.quickMoreFixedPopover) {
        this.positionQuickMorePanel(ev);
        requestAnimationFrame(() => this.positionQuickMorePanel(ev));
      } else if (!this.scorerUseKeypadQuickOverlay()) {
        this.quickMorePanelStyle = '';
        this.$nextTick(() => {
          this.$refs?.quickMorePanel?.scrollIntoView?.({
            behavior: 'smooth',
            block: 'nearest',
          });
        });
      } else {
        this.quickMorePanelStyle = '';
      }
    });
  },

  positionQuickMorePanel(ev) {
    if (!this.quickMoreFixedPopover || this.scorerUseKeypadQuickOverlay()) return;
    const btn = ev?.currentTarget;
    if (!btn?.getBoundingClientRect) return;
    const r = btn.getBoundingClientRect();
    const vw = globalThis.innerWidth || 400;
    const vh = globalThis.innerHeight || 600;
    const panelW = Math.min(300, vw - 16);
    const gap = 8;
    const panelEl = this.$refs?.quickMorePanel;
    const estRows = Math.ceil(this.moreQuickPresets().length / 4);
    const estH = Math.max(120, (estRows * 44) + 56);
    const measured = panelEl ? Math.max(panelEl.scrollHeight || 0, panelEl.offsetHeight || 0) : 0;
    const maxAllowedH = Math.max(160, vh - (gap * 2));
    const panelH = Math.min(Math.max(estH, measured), maxAllowedH);
    let left = r.left + r.width / 2 - panelW / 2;
    const roomBelow = (vh - gap) - (r.bottom + gap);
    const roomAbove = (r.top - gap) - gap;
    let top = r.bottom + gap;
    if (roomBelow < panelH && roomAbove > roomBelow) {
      top = r.top - gap - panelH;
    }
    left = Math.max(gap, Math.min(left, vw - panelW - gap));
    top = Math.max(gap, Math.min(top, vh - panelH - gap));
    this.quickMorePanelStyle = `position:fixed;left:${Math.round(left)}px;top:${Math.round(top)}px;width:${Math.round(panelW)}px;max-height:${Math.round(panelH)}px;overflow-y:auto;z-index:10050;`;
  },

  closeQuickMore() {
    this.quickMoreOpen = false;
    this.quickMorePanelStyle = '';
    this.quickMoreFixedPopover = true;
  },

  onQuickMoreClickOutside(ev) {
    const scoreBtn = this.$refs?.moreQuickBtn;
    const target = ev?.target;
    if (scoreBtn && target && scoreBtn.contains(target)) return;
    this.closeQuickMore();
  },

  pickQuickMorePreset(preset) {
    if (preset && typeof preset === 'object') return;
    this.setScoreInput(preset);
    this.closeQuickMore();
  },

  async pickQuickMoreNoScore() {
    if (!this.liveKeypadEnabled()) return;
    this.setScoreInput(0);
    this.closeQuickMore();
    await this.$nextTick();
    await this.recordThrow();
  },

  isNsQuickPreset(preset) {
    return !!(preset && typeof preset === 'object' && preset.ns);
  },

  quickMorePresetKey(preset) {
    return this.isNsQuickPreset(preset) ? 'qkm-ns' : `qkm-${preset}`;
  },

  lastThreeVisitPillsForRole(role) {
    const leg = this.currentLeg();
    const visits = (leg?.visits || []).filter((v) => String(v.playerRole) === role);
    const last3 = visits.slice(-3);
    return last3.map((v) => (v.bust ? 'Bust' : String(v.score ?? '')));
  },

  activeTurnRole() {
    return String(this.session?.liveScoring?.currentTurnRole || 'A');
  },

  /** Venue kiosk embed: Pair TV + scorer QR live in the shell rail — hide duplicate blocks in the iframe. */
  venueKioskEmbedHasSidebarRail() {
    return !!(this.embedMode && this.fromVenueKiosk);
  },

  /** Venue embed: same compact A/B row as quick-match scorer. */
  trainingEmbedCompactPairLayout() {
    return !!(this.embedMode && this.fromVenueKiosk && this.trainingShowOpponentPairCards());
  },

  trainingShowOpponentPairCards() {
    if (this.needsStart() || this.matchFinished()) return false;
    if (String(this.session?.liveScoring?.state) !== 'live') return false;
    return !this.isCatalogSolo();
  },

  trainingShowSoloHero() {
    if (this.needsStart() || this.matchFinished()) return false;
    if (String(this.session?.liveScoring?.state) !== 'live') return false;
    return this.isCatalogSolo();
  },

  catalogGoalTitle() {
    const ct = this.session?.catalogTemplate;
    if (ct?.sessionLabel) return String(ct.sessionLabel);
    return String(this.session?.title || 'Training').trim() || 'Training';
  },

  catalogGoalDescription() {
    const ct = this.session?.catalogTemplate;
    const d = typeof ct?.sessionDescription === 'string' ? ct.sessionDescription.trim() : '';
    if (d) return d;
    const t = typeof ct?.sessionTagline === 'string' ? ct.sessionTagline.trim() : '';
    if (t) return t;
    return '';
  },

  /** Narrative goal: catalog description, else session notes (non-catalog / custom). */
  trainingSessionGoalBlurb() {
    const d = this.catalogGoalDescription();
    if (d) return d;
    return String(this.session?.notes || '').trim();
  },

  passCriteriaSummary() {
    if (!this.session?.catalogTemplate) return '';
    return formatPassCriteriaHint(this.session.catalogTemplate.passCriteria);
  },

  scoringKeypadActive() {
    if (this.opponentRevealOpen) return false;
    if (this.saving || this.matchFinished()) return false;
    if (String(this.session?.liveScoring?.state) !== 'live') return false;
    const turn = this.session?.liveScoring?.currentTurnRole;
    if (!turn) return false;
    if (turn === 'A') return true;
    return turn === 'B' && String(this.session?.sourceType) === 'live_practice' && !this.isCatalogSolo();
  },

  /** Align with fixture/challenge scorer template helpers. */
  canScoreNow() {
    return this.scoringKeypadActive();
  },

  hasRequiredPlayers() {
    return !!this.session && !this.matchFinished();
  },

  dismissCheckoutSheetIfOpen() {
    if (this.checkoutSheetOpen) this.checkoutSheetOpen = false;
  },

  appendDigit(digit) {
    if (this.isPerDartDoubles()) return;
    if (!this.scoringKeypadActive()) return;
    this.dismissCheckoutSheetIfOpen();
    const nextValue = `${this.scoreInput || ''}${digit}`.slice(0, 3);
    const numeric = Math.min(180, Number(nextValue || 0));
    this.scoreInput = String(Number.isFinite(numeric) ? numeric : '');
    this.checkoutDarts = '';
  },

  setScoreInput(value) {
    if (this.isPerDartDoubles()) return;
    if (!this.scoringKeypadActive()) return;
    if (value && typeof value === 'object') return;
    this.dismissCheckoutSheetIfOpen();
    const numeric = Math.min(180, Number(value || 0));
    this.scoreInput = String(Number.isFinite(numeric) ? numeric : '');
    this.checkoutDarts = '';
  },

  clearScoreInput() {
    if (this.isPerDartDoubles()) return;
    if (!this.scoringKeypadActive()) return;
    this.dismissCheckoutSheetIfOpen();
    this.scoreInput = '';
    this.checkoutDarts = '';
  },

  backspace() {
    if (this.isPerDartDoubles()) return;
    if (!this.scoringKeypadActive()) return;
    this.dismissCheckoutSheetIfOpen();
    this.scoreInput = String(this.scoreInput || '').slice(0, -1);
    this.checkoutDarts = '';
  },

  disarmUndo() {
    this.undoArmed = false;
    if (this.undoArmTimer) {
      window.clearTimeout(this.undoArmTimer);
      this.undoArmTimer = null;
    }
  },

  armUndo() {
    this.undoArmed = true;
    this.$store?.toastr?.info?.('Tap Undo again to confirm', 1800);
    if (this.undoArmTimer) window.clearTimeout(this.undoArmTimer);
    this.undoArmTimer = window.setTimeout(() => {
      this.undoArmed = false;
      this.undoArmTimer = null;
    }, 3000);
  },

  async renderTrainingShareQr() {
    if (this.venueKioskEmbedHasSidebarRail()) return;
    const mount = this.$refs?.scorerShareQr;
    if (!mount || !this.shareTrainingScorerUrl()) return;
    if (this.scorerShareQrCanvas && !mount.contains(this.scorerShareQrCanvas)) {
      this.scorerShareQrCanvas = null;
    }
    const size = 128;
    if (!this.scorerShareQrCanvas) {
      this.scorerShareQrCanvas = document.createElement('canvas');
      this.scorerShareQrCanvas.className = 'block h-full w-full max-h-full max-w-full rounded-md';
      mount.innerHTML = '';
      mount.appendChild(this.scorerShareQrCanvas);
    }
    try {
      await renderQrWithCenterLogo(this.scorerShareQrCanvas, this.shareTrainingScorerUrl(), size);
    } catch (e) {
      console.error('Training scorer QR failed', e);
    }
  },

  roleADisplayName() {
    if (this.session?.guestKiosk === true) return 'Guest';
    const me = this.$store?.session?.me;
    if (!me) return 'You';
    const n = String(me.nickname || me.fullName || 'You').trim();
    return n || 'You';
  },

  roleATitle() {
    const me = this.$store?.session?.me;
    if (!me) return '';
    if (me.fullName && me.nickname) return `${me.fullName} (${me.nickname})`;
    return me.fullName || me.nickname || '';
  },

  roleAAvatarUrl() {
    if (this.session?.guestKiosk === true) return playerAvatarUrl({ playerId: 'training-guest' });
    return playerAvatarUrl(this.$store?.session?.me);
  },

  roleBDisplayName() {
    const st = String(this.session?.sourceType || 'live_practice');
    if (st === 'ghost_from_session') return 'Ghost';
    if (st === 'bot') {
      return String(this.session?.botMeta?.kind) === 'openai' ? 'ChatGPT' : 'Computer';
    }
    if (st === 'ai') return 'Opponent';
    if (st === 'live_practice' && this.isCatalogSolo()) return 'Solo';
    return 'Other side';
  },

  ghostSourceTitle() {
    const t = String(this.ghostSource?.title || '').trim();
    return t || '';
  },

  roleBTitle() {
    return this.modeLabel();
  },

  roleBAvatarUrl() {
    const st = String(this.session?.sourceType || 'live_practice');
    if (st === 'ghost_from_session') return '/assets/pdl-avatar-dark.svg';
    if (st === 'bot') return '/assets/pdl-avatar-light.svg';
    return '/assets/placeholder-player.png';
  },

  canUndoVisit() {
    if (this.saving || this.opponentRunning || this.opponentRevealOpen || this.matchFinished()) return false;
    if (String(this.session?.liveScoring?.state) !== 'live') return false;
    const leg = this.currentLeg();
    return Array.isArray(leg?.visits) && leg.visits.length > 0;
  },

  canEditLegVisits() {
    if (this.isPerDartDoubles()) return false;
    if (this.saving || this.opponentRunning || this.opponentRevealOpen || this.matchFinished()) return false;
    if (String(this.session?.liveScoring?.state) !== 'live') return false;
    const leg = this.currentLeg();
    return !!(leg && !leg.winnerRole);
  },

  visitDisplaySuffix(v) {
    if (!v) return '';
    const parts = [];
    const db = v.dartBreakdown;
    if (Array.isArray(db) && db.length) {
      parts.push(
        db
          .map((s) => (String(s).toLowerCase() === 'miss' ? 'Miss' : String(s).replace(/^d(\d+)$/i, (_, n) => `Double ${n}`)))
          .join(', '),
      );
    }
    if (v.bust) parts.push('Bust');
    else if (v.checkout) {
      parts.push(`CO ${v.checkoutLabel != null && v.checkoutLabel !== '' ? v.checkoutLabel : v.score}`);
    }
    return parts.join(' · ');
  },

  /** Newest visit first (last score at top); each item carries visitIndex for PATCH / blur handlers. */
  currentLegVisitsDesc() {
    const leg = this.currentLeg();
    const visits = leg?.visits;
    if (!Array.isArray(visits) || visits.length === 0) return [];
    const out = [];
    for (let i = visits.length - 1; i >= 0; i -= 1) {
      out.push({ visit: visits[i], visitIndex: i });
    }
    return out;
  },

  isLegVisitEditing(visitIndex) {
    return this.editingVisitIndex === visitIndex;
  },

  isEditingHistoryEntry(index) {
    return this.editingVisitIndex === index;
  },

  async startLegVisitEdit(entry) {
    if (!this.canEditLegVisits()) return;
    const idx = entry.visitIndex ?? entry.index;
    if (this.editingVisitIndex !== null && this.editingVisitIndex !== idx) {
      this.cancelLegVisitEdit();
    }
    this.editingVisitIndex = idx;
    this.editingVisitDraft = String(entry.visit?.score ?? '');
    await this.$nextTick();
    const el = document.querySelector('[data-training-visit-edit-input]');
    if (el instanceof HTMLInputElement) {
      el.focus({ preventScroll: true });
      el.select();
    }
  },

  cancelLegVisitEdit() {
    this.editingVisitIndex = null;
    this.editingVisitDraft = '';
  },

  async commitLegVisitEdit() {
    const visitIndex = this.editingVisitIndex;
    if (visitIndex == null) return;
    if (!this.canEditLegVisits() || this.saving) return;
    const leg = this.currentLeg();
    const v = leg?.visits?.[visitIndex];
    if (!v) {
      this.cancelLegVisitEdit();
      return;
    }
    const raw = String(this.editingVisitDraft ?? '').trim();
    const score = Math.min(180, Math.max(0, Number(raw)));
    if (!Number.isFinite(score)) {
      this.$store?.toastr?.warning?.('Enter a score from 0–180');
      return;
    }
    if (Number(v.score) === score) {
      this.cancelLegVisitEdit();
      return;
    }
    const ok = await this.patchVisitAtIndex(visitIndex, score, v);
    if (ok) this.cancelLegVisitEdit();
  },

  /** @returns {Promise<boolean>} */
  async patchVisitAtIndex(visitIndex, score, previousVisit) {
    this.saving = true;
    let ok = false;
    try {
      const body = { score };
      if (previousVisit?.checkout) {
        body.checkoutDarts = Number(previousVisit.darts) || 3;
        if (previousVisit.checkoutLabel) body.checkoutLabel = String(previousVisit.checkoutLabel);
      }
      const res = await apiFetch(`/training/sessions/${this.sessionId}/live/visit/${visitIndex}`, {
        method: 'PATCH',
        body,
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j?.ok) throw new Error(j?.error || 'Update failed');
      this.session = this.mergeSessionKeepCatalog(this.session, j.session);
      ok = true;
    } catch (e) {
      this.$store?.toastr?.error?.(e?.message || String(e));
      await this.refreshSession();
    } finally {
      this.saving = false;
    }
    await this.$nextTick();
    await this.runGhostOrBotAfterHuman();
    return ok;
  },

  startingScore() {
    const s = Number(this.session?.format?.startingScore);
    return Number.isFinite(s) ? s : 501;
  },

  checkoutRule() {
    return String(this.session?.format?.checkout || 'double-out');
  },

  async init() {
    this.initStartPerfMs = Date.now();
    console.info('[training-perf] scorer init start', {
      sessionId: this.sessionId || parseTrainingSessionId(),
    });
    let embed = false;
    try {
      const u = new URL(globalThis.location.href);
      embed = u.searchParams.get('embed') === '1';
      this.fromVenueKiosk = u.searchParams.get('from') === 'venue-kiosk';
      if (embed && !this.fromVenueKiosk && globalThis.parent !== globalThis) {
        try {
          const loc = globalThis.parent.location;
          const base = `${String(loc?.pathname || '')}${String(loc?.search || '')}`;
          if (base.includes('venue-kiosk')) this.fromVenueKiosk = true;
        } catch {
          /* cross-origin parent */
        }
      }
      this.venueKioskShellGoals =
        embed && this.fromVenueKiosk && u.searchParams.get('vkShellGoals') === '1';
    } catch {
      embed = false;
      this.fromVenueKiosk = false;
      this.venueKioskShellGoals = false;
    }
    this.embedMode = embed;
    this.applyScorerLayoutTheme(this.readScorerLayoutTheme());
    this.syncVenueKioskScorerEmbedTheme();
    this.bindScorerFullscreenListener();
    const q0 = loadScorerQuickConfig(TRAINING_SCORER_QUICK_LS);
    this.quickScoreTop = q0.top;
    this.quickScoreMore = q0.more;

    if (!this.$store.features.enabled('training') && !isTrainingScorerEmbedUrl()) {
      this.error = 'Training is disabled.';
      this.loading = false;
      return;
    }
    this.sessionId = parseTrainingSessionId();
    if (!this.sessionId) {
      this.error = 'Missing training session id';
      this.loading = false;
      return;
    }
    this.applyDedicatedChromeMode(true);
    this._vkShellMessageListener = (ev) => {
      try {
        if (!ev?.data || typeof ev.data !== 'object') return;
        const origin = String(globalThis.location?.origin || '');
        if (origin && String(ev.origin || '') !== origin) return;
        const d = ev.data;
        if (d.source === 'venue-kiosk-shell' && d.type === 'open-training-leave-dialog') {
          this.openVenueEmbedLeaveDialog();
          return;
        }
        if (this.handleVenueKioskScorerThemeMessage(d)) return;
        if (this.handleVenueKioskShellHistoryMessage?.(d)) return;
        if (d.source === 'venue-kiosk-shell' && d.type === 'venue-kiosk-rail-state') {
          this.fromVenueKiosk = true;
          if (typeof d.expanded === 'boolean') this.venueRailExpanded = d.expanded;
        }
      } catch {
        /* ignore */
      }
    };
    globalThis.window?.addEventListener('message', this._vkShellMessageListener);
    if (this.handleKeydown) {
      window.removeEventListener('keydown', this.handleKeydown, true);
    }
    this.handleKeydown = (e) => this.onTrainingScoreKeydown(e);
    window.addEventListener('keydown', this.handleKeydown, true);
    this.onQrResize = () => {
      if (this.qrResizeTimer) clearTimeout(this.qrResizeTimer);
      this.qrResizeTimer = setTimeout(() => {
        scheduleIdleTask(() => {
          void this.renderTrainingShareQr();
        });
        if (this.quickMoreOpen && this.quickMoreFixedPopover) {
          this.positionQuickMorePanel({ currentTarget: this.$refs?.moreQuickBtn });
        }
      }, 200);
    };
    window.addEventListener('resize', this.onQrResize);
    window.addEventListener('beforeunload', () => this.teardown());
    try {
      await this.refreshSession('init');
      if (this.session?.sourceType === 'ghost_from_session' && this.session?.ghostSourceId) {
        await this.loadGhostSource();
      }
      await this.maybeAutoStartSoloSession();
      this.$nextTick(() => this.maybeAutoOpponent());
    } catch (e) {
      this.error = e?.message || String(e);
    } finally {
      this.loading = false;
      console.info('[training-perf] scorer init done', {
        sessionId: this.sessionId,
        elapsedMs: Date.now() - this.initStartPerfMs,
      });
    }
    await this.$nextTick();
    await this.$nextTick();
    scheduleIdleTask(() => {
      void this.renderTrainingShareQr();
    });
  },

  isKeyboardTargetEditable(target) {
    if (!target || !(target instanceof HTMLElement)) return false;
    const tag = String(target.tagName || '').toLowerCase();
    if (target.isContentEditable) return true;
    if ((tag === 'input' || tag === 'textarea') && target.hasAttribute('readonly')) return false;
    return ['input', 'select', 'textarea'].includes(tag);
  },

  onTrainingScoreKeydown(event) {
    if (this.loading || !this.session) return;
    if (this.opponentRevealOpen) return;
    const key = String(event.key || '');
    if (key === 'Escape') {
      if (this.playerStatsOpen) {
        event.preventDefault();
        this.closePlayerStatsDialog();
        return;
      }
      if (this.quickMoreOpen) {
        event.preventDefault();
        this.closeQuickMore();
        return;
      }
    }
    const activeEl = document.activeElement;
    if (activeEl instanceof HTMLElement && this.isKeyboardTargetEditable(activeEl)) return;
    if (this.isKeyboardTargetEditable(event.target)) return;
    if (event.repeat || event.defaultPrevented) return;
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    if (/^[0-9]$/.test(key)) {
      event.preventDefault();
      event.stopPropagation();
      this.appendDigit(key);
      return;
    }
    if (/^Numpad[0-9]$/.test(key)) {
      event.preventDefault();
      event.stopPropagation();
      this.appendDigit(key.replace('Numpad', ''));
      return;
    }
    if (key === 'Backspace') {
      event.preventDefault();
      event.stopPropagation();
      this.backspace();
      return;
    }
    if (key === 'Enter' || key === 'NumpadEnter') {
      // Main score field is readonly but uses @keydown.enter → onRecordPress(); bubbling would duplicate.
      const ae = document.activeElement;
      if (ae instanceof HTMLInputElement && ae.readOnly) return;
      if (ae instanceof HTMLTextAreaElement && ae.readOnly) return;
      event.preventDefault();
      void this.onRecordPress();
    }
  },

  teardown() {
    this.closeQuickMore();
    this.disarmUndo();
    this.opponentRevealOpen = false;
    this.venueEmbedLeaveDialogOpen = false;
    this.checkoutSheetOpen = false;
    this.cancelLegVisitEdit();
    if (typeof document !== 'undefined' && this._scrollLockApplied) {
      const body = document.body;
      const docEl = document.documentElement;
      if (body && docEl) {
        body.style.overflow = this._prevBodyOverflow || '';
        body.style.touchAction = this._prevBodyTouchAction || '';
        body.style.overscrollBehavior = this._prevBodyOverscroll || '';
        docEl.style.overflow = this._prevDocOverflow || '';
        docEl.style.overscrollBehavior = this._prevDocOverscroll || '';
      }
      this._scrollLockApplied = false;
    }
    this.applyDedicatedChromeMode(false);
    this.opponentRunning = false;
    if (this._vkShellMessageListener) {
      globalThis.window?.removeEventListener('message', this._vkShellMessageListener);
      this._vkShellMessageListener = null;
    }
    if (this.handleKeydown) {
      window.removeEventListener('keydown', this.handleKeydown, true);
      this.handleKeydown = null;
    }
    if (this.onQrResize) {
      window.removeEventListener('resize', this.onQrResize);
      this.onQrResize = null;
    }
    if (this.qrResizeTimer) {
      clearTimeout(this.qrResizeTimer);
      this.qrResizeTimer = null;
    }
    this.unbindScorerFullscreenListener();
    if (this.autoRunTimer) {
      clearTimeout(this.autoRunTimer);
      this.autoRunTimer = null;
    }
    this.scorerShareQrCanvas = null;
  },

  async refreshSession(reason = 'refresh') {
    const fetchStartedAt = Date.now();
    const res = await apiFetch(`/training/sessions/${this.sessionId}`);
    const j = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(j?.error || 'Failed to load session');
    this.session = this.mergeSessionKeepCatalog(this.session, j?.session || j);
    console.info('[training-perf] scorer session fetch done', {
      sessionId: this.sessionId,
      reason,
      elapsedMs: Date.now() - fetchStartedAt,
    });
    if (this.isPerDartDoubles()) this.resetPerDartForm();
    this.syncVenueKioskTrainingRailMeta();
  },

  syncVenueKioskTrainingRailMeta() {
    if (!this.venueKioskEmbedHasSidebarRail() || !this.session) return;
    this.postVenueKioskShellMessage({
      type: 'training-rail-meta',
      meta: {
        goalTitle: this.catalogGoalTitle(),
        goalDescription: this.trainingSessionGoalBlurb(),
        passSummary: this.passCriteriaSummary(),
        computerOpponentSummary: this.isRuleBot() ? this.ruleBotLevelReadOnlySummary() : '',
        profileNote: this.trainingProfileProgressNote(),
      },
    });
  },

  async loadGhostSource() {
    const gid = this.session?.ghostSourceId;
    if (!gid) return;
    const res = await apiFetch(`/training/sessions/${gid}`);
    const j = await res.json().catch(() => ({}));
    if (!res.ok) return;
    this.ghostSource = j?.session || j;
  },

  /** Legs needed to win (from best-of). */
  firstToLegs() {
    const b = Math.max(1, Number(this.session?.format?.bestOfLegs) || 9);
    return Math.ceil(b / 2);
  },

  currentLegNumberDisplay() {
    return Number(this.session?.liveScoring?.currentLegNumber) || 1;
  },

  modeLabel() {
    const t = String(this.session?.sourceType || 'live_practice');
    if (t === 'ghost_from_session') return 'Ghost replay';
    if (t === 'bot') {
      return String(this.session?.botMeta?.kind) === 'openai' ? 'ChatGPT opponent' : 'Computer opponent';
    }
    if (t === 'ai') return 'Sample leg (review)';
    if (t === 'live_practice') return this.isCatalogSolo() ? 'Solo practice' : 'Practice (A / B turns)';
    return 'Practice';
  },

  isOpenAiBot() {
    return String(this.session?.sourceType) === 'bot' && String(this.session?.botMeta?.kind) === 'openai';
  },

  isRuleBot() {
    return String(this.session?.sourceType) === 'bot' && String(this.session?.botMeta?.kind) !== 'openai';
  },

  opponentThinkingHeadline() {
    return this.isOpenAiBot() ? 'ChatGPT is thinking…' : 'Computer is thinking…';
  },

  opponentRevealSourceLabel() {
    return this.opponentRevealSource === 'chatgpt' ? 'ChatGPT' : 'Computer';
  },

  computeScoreReaction(score) {
    const s = Math.max(0, Math.min(180, Math.round(Number(score) || 0)));
    if (s === 180) return { headline: 'WOW!', sub: 'Maximum 180!', tone: 'max' };
    if (s >= 171) return { headline: 'Big fish!', sub: '171 — seven mark!', tone: 'hot' };
    if (s >= 140) return { headline: 'Huge!', sub: 'Monster visit', tone: 'hot' };
    if (s >= 120) return { headline: 'Brilliant!', sub: 'That\'s scoring', tone: 'great' };
    if (s >= 100) return { headline: 'Ton-up!', sub: '100 or more', tone: 'ton' };
    if (s >= 80) return { headline: 'Solid!', sub: 'Nice scoring', tone: 'solid' };
    if (s >= 60) return { headline: 'Good shot', sub: 'Keeping the pressure on', tone: 'nice' };
    return { headline: 'Score', sub: 'Visit recorded', tone: 'quiet' };
  },

  async showOpponentScoreReveal(score, source) {
    const s = Math.max(0, Math.min(180, Math.round(Number(score) || 0)));
    const r = this.computeScoreReaction(s);
    this.opponentRevealScore = s;
    this.opponentRevealHeadline = r.headline;
    this.opponentRevealSub = r.sub;
    this.opponentRevealTone = r.tone;
    this.opponentRevealSource = source === 'openai' ? 'chatgpt' : 'computer';
    this.opponentRevealOpen = true;
    await this.$nextTick();
    const ms = s >= 140 ? 3000 : s >= 100 ? 2600 : 2000;
    await sleep(ms);
    this.opponentRevealOpen = false;
  },

  opponentRevealPanelClass() {
    const t = this.opponentRevealTone;
    if (t === 'max') {
      return 'border-amber-400/50 bg-gradient-to-br from-amber-900/50 via-rose-950/40 to-neutral-950 ring-2 ring-amber-400/70';
    }
    if (t === 'hot') {
      return 'border-orange-500/40 bg-gradient-to-br from-orange-950/50 to-neutral-950 ring-1 ring-orange-500/35';
    }
    if (t === 'great') {
      return 'border-amber-500/30 bg-gradient-to-br from-amber-950/40 to-neutral-950 ring-1 ring-amber-500/25';
    }
    if (t === 'ton') {
      return 'border-emerald-500/35 bg-gradient-to-br from-emerald-950/45 to-neutral-950 ring-1 ring-emerald-500/30';
    }
    if (t === 'solid' || t === 'nice') {
      return 'border-sky-500/30 bg-gradient-to-br from-sky-950/35 to-neutral-950 ring-1 ring-sky-500/25';
    }
    return 'border-white/10 bg-neutral-900/90 ring-1 ring-white/10';
  },

  ruleBotLevel() {
    const fromSession = Number(this.session?.botMeta?.ruleBotLevel);
    if (Number.isFinite(fromSession)) return Math.min(10, Math.max(1, Math.round(fromSession)));
    const fromCatalog = Number(this.session?.catalogTemplate?.computerRuleBotLevel);
    if (Number.isFinite(fromCatalog)) return Math.min(10, Math.max(1, Math.round(fromCatalog)));
    return 5;
  },

  ruleBotLevelLabel() {
    const n = this.ruleBotLevel();
    if (n <= 2) return 'Beginner';
    if (n <= 4) return 'Casual';
    if (n <= 6) return 'Medium';
    if (n <= 8) return 'Hard';
    return 'Pro';
  },

  /** Read-only line for scorer UI (strength comes from catalog / session creation, not editable here). */
  ruleBotLevelReadOnlySummary() {
    const n = this.ruleBotLevel();
    return `${n} · ${this.ruleBotLevelLabel()}`;
  },

  /** Shown on solo and match layouts: ties training to profile / badges (future player profile). */
  trainingProfileProgressNote() {
    return 'When you complete this session, progress counts toward your training goals and badges and will show on your player profile.';
  },

  currentLeg() {
    const legNo = Number(this.session?.liveScoring?.currentLegNumber) || 1;
    const legs = this.session?.liveScoring?.legs || [];
    return legs.find((l) => Number(l.legNumber) === legNo) || null;
  },

  remainingForRole(role) {
    const leg = this.currentLeg();
    const visits = leg?.visits || [];
    let rem = this.startingScore();
    for (const v of visits) {
      if (String(v.playerRole) !== role) continue;
      const post = Number(v?.postScore);
      if (Number.isFinite(post)) {
        rem = post;
        continue;
      }
      if (v?.bust) continue;
      const scored = Math.max(0, Number(v?.score || 0));
      rem = Math.max(0, rem - scored);
    }
    return rem;
  },

  /** Align layout with fixture/challenge "home" / "away" scorer cards. */
  currentTurnSide() {
    const t = String(this.session?.liveScoring?.currentTurnRole || 'A');
    return t === 'B' ? 'away' : 'home';
  },

  remainingForSide(side) {
    return this.remainingForRole(side === 'home' ? 'A' : 'B');
  },

  sidePlayerLabel(side) {
    return side === 'home' ? this.roleADisplayName() : this.roleBDisplayName();
  },

  sidePlayerAvatar(side) {
    return side === 'home' ? this.roleAAvatarUrl() : this.roleBAvatarUrl();
  },

  legPointsHome() {
    return Number(this.session?.liveScoring?.legsWonA) || 0;
  },

  legPointsAway() {
    return Number(this.session?.liveScoring?.legsWonB) || 0;
  },

  trainingListUrl() {
    return '/admin/training';
  },

  trainingLaunchUrl() {
    const id = parseTrainingSessionId();
    return id ? `/admin/training/${id}/launch` : '/admin/training';
  },

  /** Matches challenge scorer: ring + tinted bg for the side whose turn it is. */
  turnCardActiveClass(role) {
    const inactive = 'bg-neutral-900/50 dark:bg-neutral-950/40';
    if (this.matchFinished()) return inactive;
    if (String(this.session?.liveScoring?.state) !== 'live') return inactive;
    const turn = String(this.session?.liveScoring?.currentTurnRole || '');
    const r = String(role);
    if (turn !== r) return inactive;
    if (r === 'A') {
      return 'bg-emerald-50 ring-2 ring-emerald-400/70 dark:bg-emerald-950/30 dark:ring-emerald-500/40';
    }
    return 'bg-amber-50 ring-2 ring-amber-400/70 dark:bg-amber-950/30 dark:ring-amber-500/40';
  },

  currentTurnRemaining() {
    const turn = this.session?.liveScoring?.currentTurnRole;
    if (!turn) return this.startingScore();
    return this.remainingForRole(turn);
  },

  scoreOutcome() {
    const score = this.isPerDartDoubles()
      ? this.perDartVisitScore()
      : Number(this.scoreInput || 0);
    if (!Number.isFinite(score) || score < 0) {
      return { valid: false, checkout: false, busted: false, remaining: this.currentTurnRemaining() };
    }
    const remaining = this.currentTurnRemaining();
    const nextRemaining = Math.max(0, remaining - score);
    const busted = score > remaining || nextRemaining === 1;
    const checkout = !busted && nextRemaining === 0;
    return { valid: true, checkout, busted, remaining: nextRemaining };
  },

  /** Sticky Record bar while the human can score (A turn or live practice B). */
  showScoringFooter() {
    if (this.matchFinished()) return false;
    if (String(this.session?.liveScoring?.state) !== 'live') return false;
    const turn = this.session?.liveScoring?.currentTurnRole;
    if (!turn) return false;
    if (turn === 'A') return true;
    return turn === 'B' && String(this.session?.sourceType) === 'live_practice' && !this.isCatalogSolo();
  },

  checkoutRuleShortLabel() {
    const c = String(this.session?.format?.checkout || 'double-out');
    if (c === 'double-out') return 'Double out';
    if (c === 'master-out') return 'Master out';
    if (c === 'straight-out') return 'Straight out';
    return c.replace(/-/g, ' ');
  },

  canSubmitVisit() {
    if (!this.scoringKeypadActive()) return false;
    if (this.isPerDartDoubles()) {
      if (!this.buildDartBreakdownPayload()) return false;
    } else if (!String(this.scoreInput || '').trim()) {
      return false;
    }
    const o = this.scoreOutcome();
    if (!o.valid || o.busted) return false;
    return true;
  },

  /** Enter / Submit — same flow as fixture & challenge scorers (inline checkout darts). */
  async onRecordPress() {
    if (!this.canSubmitVisit()) return;
    const outcome = this.scoreOutcome();
    if (outcome.checkout) {
      this.openCheckoutConfirmDialog();
      return;
    }
    await this.recordThrow();
  },

  openCheckoutConfirmDialog() {
    this.checkoutConfirmPending = {
      scoreInput: String(this.scoreInput ?? ''),
      checkoutDarts: String(this.checkoutDarts || ''),
      checkoutLabel: String(this.checkoutLabel || ''),
    };
    this.checkoutConfirmDarts = ['1', '2', '3'].includes(String(this.checkoutDarts || ''))
      ? String(this.checkoutDarts)
      : '3';
    this.checkoutConfirmOpen = true;
  },

  cancelCheckoutConfirm() {
    const pending = this.checkoutConfirmPending || null;
    if (pending) {
      this.scoreInput = String(pending.scoreInput ?? '');
      this.checkoutDarts = String(pending.checkoutDarts || '');
      this.checkoutLabel = String(pending.checkoutLabel || '');
    }
    this.checkoutConfirmPending = null;
    this.checkoutConfirmOpen = false;
  },

  async confirmCheckoutConfirm() {
    if (!this.checkoutConfirmOpen) return;
    this.checkoutDarts = String(this.checkoutConfirmDarts || '3');
    this.checkoutConfirmPending = null;
    this.checkoutConfirmOpen = false;
    await this.recordThrow({ skipCheckoutConfirm: true });
  },

  matchFinished() {
    return String(this.session?.liveScoring?.state) === 'finished' || this.sessionWon();
  },

  shouldAutoStartSoloSession() {
    if (!this.needsStart()) return false;
    if (!this.isCatalogSolo()) return false;
    return String(this.session?.sourceType || '') === 'live_practice';
  },

  async maybeAutoStartSoloSession() {
    if (!this.shouldAutoStartSoloSession()) return;
    await this.startMatch({ silent: true, reason: 'auto_solo' });
  },

  sessionWon() {
    const need = legsToWin(this.session?.format?.bestOfLegs);
    const a = Number(this.session?.liveScoring?.legsWonA) || 0;
    const b = Number(this.session?.liveScoring?.legsWonB) || 0;
    return a >= need || b >= need;
  },

  needsStart() {
    const ls = this.session?.liveScoring;
    if (!ls) return true;
    if (String(ls.state) === 'finished') return false;
    return !Array.isArray(ls.legs) || ls.legs.length === 0;
  },

  needsNextLeg() {
    const ls = this.session?.liveScoring;
    if (!ls || String(ls.state) !== 'live') return false;
    const leg = this.currentLeg();
    if (!leg?.winnerRole) return false;
    if (this.sessionWon()) return false;
    return true;
  },

  /** Source session leg we are replaying B from (same leg number as our session). */
  ghostReplaySourceLeg() {
    if (!this.ghostSource?.liveScoring?.legs) return null;
    const legNo = Number(this.session?.liveScoring?.currentLegNumber) || 1;
    return this.ghostSource.liveScoring.legs.find((l) => Number(l.legNumber) === legNo) || null;
  },

  ghostBQueue() {
    const leg = this.ghostReplaySourceLeg();
    if (!leg) return [];
    return (leg.visits || []).filter((v) => String(v.playerRole) === 'B');
  },

  /**
   * Index into ghostBQueue() for the next B visit to post.
   * Ghost sessions start leg 1 as A; the tape may start with B — then q[0] is a B thrown
   * before the tape’s first A, so our first B after our first A must use q[1].
   */
  ghostBQueueIndexForNextVisit() {
    const ourLeg = this.currentLeg();
    const srcLeg = this.ghostReplaySourceLeg();
    if (!ourLeg || !srcLeg) return 0;
    const k = this.countBVisitsInCurrentLeg();
    const ourStart = String(ourLeg.startingRole || 'A');
    const srcStart = String(srcLeg.startingRole || 'A');
    if (ourStart === 'A' && srcStart === 'B') return k + 1;
    return k;
  },

  countBVisitsInCurrentLeg() {
    const leg = this.currentLeg();
    return (leg?.visits || []).filter((v) => String(v.playerRole) === 'B').length;
  },

  async startMatch(options = {}) {
    const silent = options && options.silent === true;
    const reason = String(options?.reason || 'manual');
    this.saving = true;
    try {
      const res = await apiFetch(`/training/sessions/${this.sessionId}/live/start`, {
        method: 'POST',
        body: { startingRole: 'A' },
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j?.error || 'Start failed');
      this.session = this.mergeSessionKeepCatalog(this.session, j.session);
      if (!silent) this.$store?.toastr?.success?.('Session started');
      console.info('[training-perf] scorer session started', {
        sessionId: this.sessionId,
        reason,
      });
      this.maybeAutoOpponent();
      await this.$nextTick();
      scheduleIdleTask(() => {
        void this.renderTrainingShareQr();
      });
    } catch (e) {
      if (!silent) this.$store?.toastr?.error?.(e?.message || String(e));
      else console.warn('[training-perf] scorer auto-start failed', {
        sessionId: this.sessionId,
        message: String(e?.message || e || ''),
      });
    } finally {
      this.saving = false;
    }
  },

  async recordThrow(opts = {}) {
    if (!this.canSubmitVisit()) return;
    this.disarmUndo();
    const o = this.scoreOutcome();
    if (o.checkout && !opts?.skipCheckoutConfirm) {
      this.openCheckoutConfirmDialog();
      return;
    }
    let checkoutRecorded = false;
    this.saving = true;
    try {
      const body = this.isPerDartDoubles()
        ? {
            score: this.perDartVisitScore(),
            dartBreakdown: this.buildDartBreakdownPayload(),
            checkoutDarts: o.checkout ? (Number(this.checkoutDarts) || 3) : undefined,
            checkoutLabel: o.checkout ? String(this.checkoutLabel || '').trim() || undefined : undefined,
          }
        : {
            score: Number(this.scoreInput) || 0,
            checkoutDarts: o.checkout ? (Number(this.checkoutDarts) || 3) : undefined,
            checkoutLabel: o.checkout
              ? (String(this.checkoutLabel || '').trim() || String(Number(this.scoreInput) || 0))
              : undefined,
          };
      const res = await apiFetch(`/training/sessions/${this.sessionId}/live/visit`, {
        method: 'POST',
        body,
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j?.ok) throw new Error(j?.error || 'Failed to record');
      this.session = this.mergeSessionKeepCatalog(this.session, j.session);
      this.scoreInput = '';
      this.checkoutDarts = '';
      this.checkoutLabel = '';
      if (this.isPerDartDoubles()) this.resetPerDartForm();
      checkoutRecorded = !!o.checkout;
    } catch (e) {
      this.$store?.toastr?.error?.(e?.message || String(e));
    } finally {
      this.saving = false;
    }
    if (checkoutRecorded) {
      await this.nextLeg();
      return;
    }
    await this.$nextTick();
    await this.runGhostOrBotAfterHuman();
  },

  async undoThrow() {
    if (!this.canUndoVisit()) return;
    if (!this.undoArmed) {
      this.armUndo();
      return;
    }
    this.disarmUndo();
    this.checkoutSheetOpen = false;
    this.saving = true;
    try {
      const res = await apiFetch(`/training/sessions/${this.sessionId}/live/undo`, { method: 'POST' });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j?.ok) throw new Error(j?.error || 'Undo failed');
      this.session = this.mergeSessionKeepCatalog(this.session, j.session);
      this.$store?.toastr?.success?.('Last score undone', 1800);
    } catch (e) {
      this.$store?.toastr?.error?.(e?.message || String(e));
    } finally {
      this.saving = false;
    }
  },

  async nextLeg() {
    this.saving = true;
    try {
      const res = await apiFetch(`/training/sessions/${this.sessionId}/live/next-leg`, { method: 'POST' });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j?.ok) throw new Error(j?.error || 'Next leg failed');
      this.session = this.mergeSessionKeepCatalog(this.session, j.session);
      this.maybeAutoOpponent();
    } catch (e) {
      this.$store?.toastr?.error?.(e?.message || String(e));
    } finally {
      this.saving = false;
    }
  },

  maybeAutoOpponent() {
    if (this.autoRunTimer) clearTimeout(this.autoRunTimer);
    this.autoRunTimer = setTimeout(() => this.runGhostOrBot(), 50);
  },

  /** Run right after the human submits a visit so B throws next without waiting on timers/saving races. */
  async runGhostOrBotAfterHuman() {
    if (this.opponentRunning) return;
    if (!this.session || this.matchFinished()) return;
    if (String(this.session.liveScoring?.state) !== 'live') return;
    if (this.session.liveScoring?.currentTurnRole !== 'B') return;
    const st = String(this.session.sourceType || '');
    if (st === 'ghost_from_session') {
      await sleep(180);
      await this.playGhostB();
    } else if (st === 'bot') {
      await sleep(120);
      await this.playBotB();
    }
  },

  async runGhostOrBot() {
    if (this.opponentRunning) return;
    if (!this.session || this.matchFinished()) return;
    if (String(this.session.liveScoring?.state) !== 'live') return;
    if (this.session.liveScoring?.currentTurnRole !== 'B') return;
    const st = String(this.session.sourceType || '');
    if (st === 'ghost_from_session') {
      await sleep(300);
      await this.playGhostB();
    } else if (st === 'bot') {
      await sleep(200);
      await this.playBotB();
    }
  },

  async playGhostB() {
    if (this.opponentRunning) return;
    const q = this.ghostBQueue();
    const idx = this.ghostBQueueIndexForNextVisit();
    const next = q[idx];
    if (!next) {
      this.$store?.toastr?.warning?.('No more ghost B visits for this leg — enter B scores manually.', 4000);
      return;
    }
    this.opponentRunning = true;
    try {
      const res = await apiFetch(`/training/sessions/${this.sessionId}/live/visit`, {
        method: 'POST',
        body: {
          score: next.score,
          playerRole: 'B',
          checkoutDarts: next.checkout ? 3 : undefined,
          checkoutLabel: next.checkoutLabel || undefined,
        },
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j?.ok) throw new Error(j?.error || 'Ghost visit failed');
      this.session = this.mergeSessionKeepCatalog(this.session, j.session);
      await this.$nextTick();
      await this.runGhostOrBotAfterHuman();
    } catch (e) {
      this.$store?.toastr?.error?.(e?.message || String(e));
    } finally {
      this.opponentRunning = false;
    }
  },

  async playBotB() {
    if (this.opponentRunning) return;
    if (this.isOpenAiBot()) {
      return this.playOpenAiBotB();
    }
    const rem = this.remainingForRole('B');
    const score = nextBotScoreClient(rem, this.checkoutRule(), this.ruleBotLevel());
    this.opponentRunning = true;
    try {
      await withOpponentThinkingUi(
        OPPONENT_THINKING_MIN_MS,
        (open) => {
          this.opponentThinkingDialogOpen = !!open;
        },
        async () => {
          const res = await apiFetch(`/training/sessions/${this.sessionId}/live/visit`, {
            method: 'POST',
            body: {
              score,
              playerRole: 'B',
            },
          });
          const j = await res.json().catch(() => ({}));
          if (!res.ok || !j?.ok) throw new Error(j?.error || 'Bot visit failed');
          this.session = this.mergeSessionKeepCatalog(this.session, j.session);
        },
      );
      await this.showOpponentScoreReveal(score, 'rule');
      await this.$nextTick();
      await this.runGhostOrBotAfterHuman();
    } catch (e) {
      this.$store?.toastr?.error?.(e?.message || String(e));
    } finally {
      this.opponentRunning = false;
    }
  },

  async playOpenAiBotB() {
    if (this.opponentRunning) return;
    this.opponentRunning = true;
    try {
      await withOpponentThinkingUi(
        OPPONENT_THINKING_MIN_MS,
        (open) => {
          this.opponentThinkingDialogOpen = !!open;
        },
        async () => {
          const res = await apiFetch(`/training/sessions/${this.sessionId}/live/visit-openai-bot`, {
            method: 'POST',
            timeoutMs: 120_000,
          });
          const j = await res.json().catch(() => ({}));
          if (!res.ok || !j?.ok) throw new Error(j?.error || 'ChatGPT API bot visit failed');
          this.session = this.mergeSessionKeepCatalog(this.session, j.session);
        },
      );
      const leg = this.currentLeg();
      const visits = leg?.visits || [];
      const last = visits[visits.length - 1];
      const lastScore =
        last && String(last.playerRole) === 'B' ? Math.round(Number(last.score) || 0) : 0;
      await this.showOpponentScoreReveal(lastScore, 'openai');
      await this.$nextTick();
      await this.runGhostOrBotAfterHuman();
    } catch (e) {
      this.$store?.toastr?.error?.(e?.message || String(e));
    } finally {
      this.opponentRunning = false;
    }
  },

  venueKioskHomeHref() {
    return `${resolveVenueKioskAppOrigin()}/venue-kiosk`;
  },

  isVenueKioskEmbed() {
    if (!this.embedMode) return false;
    if (this.fromVenueKiosk) return true;
    try {
      return globalThis.parent !== globalThis;
    } catch {
      return false;
    }
  },

  postVenueKioskShellMessage(payload) {
    const origin = globalThis.location?.origin || '*';
    try {
      if (globalThis.parent !== globalThis) {
        globalThis.parent.postMessage({ source: 'darts-scorer-embed', ...payload }, origin);
      }
    } catch {
      /* ignore */
    }
  },

  toggleVenueKioskShellRailFromEmbed() {
    if (!this.isVenueKioskEmbed()) return;
    const nextExpanded = !this.venueRailExpanded;
    this.venueRailExpanded = nextExpanded;
    this.postVenueKioskShellMessage({ type: 'toggle-venue-kiosk-rail', expanded: nextExpanded });
  },

  openVenueEmbedLeaveDialog() {
    if (!this.isVenueKioskEmbed()) return;
    this.closeScorerHeaderMenu();
    this.venueEmbedLeaveDialogOpen = true;
  },

  openMainMenuFromEmbed() {
    if (!this.isVenueKioskEmbed()) return;
    this.postVenueKioskShellMessage({ type: 'open-main-menu' });
  },

  closeVenueEmbedLeaveDialog() {
    this.venueEmbedLeaveDialogOpen = false;
  },

  confirmVenueEmbedLeaveToTrainingHub() {
    if (!this.isVenueKioskEmbed()) return;
    this.venueEmbedLeaveDialogOpen = false;
    this.teardown();
    this.postVenueKioskShellMessage({ type: 'training-scorer-leave' });
    if (globalThis.parent === globalThis) {
      globalThis.location.href = this.venueKioskHomeHref();
    }
  },

  backToList() {
    const r = window.PineconeRouter;
    if (r?.navigate) r.navigate('/admin/training');
    else window.location.href = '/admin/training';
  },

  statsCarouselRoster() {
    const out = [{
      id: 'A',
      name: this.roleADisplayName(),
      side: 'home',
      avatarUrl: this.roleAAvatarUrl(),
    }];
    if (!this.isCatalogSolo()) {
      out.push({
        id: 'B',
        name: this.roleBDisplayName(),
        side: 'away',
        avatarUrl: this.roleBAvatarUrl(),
      });
    }
    return out;
  },

  computePlayerMatchStats(role) {
    const r = String(role || '');
    if (!r) return null;
    let darts = 0;
    let points = 0;
    let n180 = 0;
    let n140 = 0;
    let n100 = 0;
    let checkouts = 0;
    for (const leg of this.session?.liveScoring?.legs || []) {
      for (const v of leg.visits || []) {
        if (String(v.playerRole) !== r) continue;
        const dv = Math.max(1, Math.min(3, Number(v.darts) || 3));
        darts += dv;
        if (v.bust) continue;
        const sc = Math.min(180, Math.max(0, Number(v.score) || 0));
        points += sc;
        if (v.checkout) checkouts += 1;
        if (sc === 180) n180 += 1;
        else if (sc >= 140) n140 += 1;
        else if (sc >= 100) n100 += 1;
      }
    }
    const legsWon = r === 'A' ? this.legPointsHome() : this.legPointsAway();
    const threeDartAvg = darts > 0 ? (points / darts) * 3 : null;
    return { darts, points, threeDartAvg, n180, n140, n100, checkouts, legsWon };
  },

  ...createScorerMatchScoreboardBindings({
    useLayout() {
      return this.trainingShowOpponentPairCards();
    },
    isLive() {
      return !this.matchFinished() && String(this.session?.liveScoring?.state) === 'live';
    },
    sideRole(side) {
      return side === 'home' ? 'Side A' : 'Side B';
    },
    legsForSide(side) {
      return side === 'home' ? this.legPointsHome() : this.legPointsAway();
    },
    centerRaceLabel() {
      return this.trainingStructure() === 'sets' ? 'Sets' : 'Legs';
    },
    centerSubline() {
      return this.headerRaceLeftText();
    },
    showVisitPills() {
      return true;
    },
    visitPillsForSide(side) {
      return this.lastThreeVisitPillsForRole(side === 'home' ? 'A' : 'B');
    },
    onTapAvatar(side) {
      this.openPlayerStatsForPlayer(side === 'home' ? 'A' : 'B');
    },
  }),

  ...createScorerPlayerStatsCarouselMethods(),
}));
